import React, { useState, useEffect, useMemo } from 'react';
import { NavBar } from '../components/nav-bar';
import { AssetBox } from '../components/asset-box';
import { db, products_col_name, categories_col_name } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import '../App.css';
import { Footer } from '../components/footer';
import { useTranslation } from "react-i18next";
import { brandName } from '../modules/modules';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 30;
const AGE_RATES = ['Child', 'Adult', 'Senior'];

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [parentFilter, setParentFilter] = useState('all'); // 'all' | 'ageRates'
    const [subFilter, setSubFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useTranslation();
    const { currentUser } = useAuth();



    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const q = query(
                    collection(db, products_col_name),
                    orderBy('createdAt', 'desc')
                );
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    image: doc.data().coverImage || doc.data().image
                }));
                setProducts(data);
            } catch (err) {
                console.error('Error fetching products:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const snapshot = await getDocs(collection(db, categories_col_name));
                const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCategories(list);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    const filteredProducts = useMemo(() => {
        let result = products;

        // Search by product name
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(p =>
                (p.name || '').toLowerCase().includes(q)
            );
        }

        // Parent filter: All or Age Rates
        if (parentFilter === 'ageRates') {
            if (subFilter && subFilter !== 'all') {
                result = result.filter(p =>
                    (p.ageRate || '').toLowerCase() === subFilter.toLowerCase()
                );
            }
        } else {
            // parentFilter === 'all' → use category sub-filter
            if (subFilter && subFilter !== 'all') {
                result = result.filter(p =>
                    (p.category || '').toLowerCase().trim() === subFilter.toLowerCase().trim()
                );
            }
        }

        return result;
    }, [products, searchQuery, parentFilter, subFilter]);

    const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE) || 1;
    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredProducts, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, parentFilter, subFilter]);

    const handleParentChange = (val) => {
        setParentFilter(val);
        setSubFilter('all');
    };

    const subOptions = parentFilter === 'ageRates'
        ? [{ id: 'all', name: 'All' }, ...AGE_RATES.map(a => ({ id: a, name: a }))]
        : [{ id: 'all', name: 'All' }, ...categories.map(c => ({ id: c.name, name: c.name }))];

    return (
        <>
            <hamlet>
                <title>products</title>
            </hamlet>
            <header>
                <div className="brand-title">
                    <h1>{brandName}</h1>
                </div>
                <div className="fixed-header">
                    <NavBar is_product_page="#0303fc" />
                </div>
            </header>

            <main className="products-page">
                {/* <section className="products-page__hero">
                    <h1 className="products-page__title">Our Products</h1>
                    <p className="products-page__subtitle">Discover our collection of elegant abayas</p>
                </section> */}

                <section className="products-page__content">
                    <div className="products-page__toolbar">
                        <div className="products-page__search">
                            <svg className="products-page__search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input
                                type="search"
                                placeholder={t("search")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="products-page__search-input"
                                aria-label="Search products by name"
                            />
                        </div>

                        <div className="products-page__filters">
                            {/* <div className="products-page__parent-filters" role="tablist">
                                <button
                                    type="button"
                                    className={`products-page__parent-pill ${parentFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => handleParentChange('all')}
                                >
                                    {t("all")}
                                </button>
                                <button
                                    type="button"
                                    className={`products-page__parent-pill ${parentFilter === 'ageRates' ? 'active' : ''}`}
                                    onClick={() => handleParentChange('ageRates')}
                                >
                                    Age Rates
                                </button>
                            </div> */}

                            <div className="products-page__sub-filters" role="tablist">
                                {subOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        className={`products-page__sub-pill ${subFilter === opt.id ? 'active' : ''}`}
                                        onClick={() => setSubFilter(opt.id)}
                                    >
                                        {opt.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="products-page__loading">
                            <div className="products-page__spinner"></div>
                            <p>Loading products...</p>
                        </div>
                    ) : (
                        <>
                            {currentUser &&
                                <p className="products-page__count">
                                    {filteredProducts.length} product{filteredProducts.length.toLocaleString() !== 1 ? 's' : ''} found
                                </p>
                            }
                            <div className="products-page__grid">
                                {paginatedProducts.length > 0 ? (
                                    paginatedProducts.map(product => (
                                        <AssetBox
                                            key={product.id}
                                            product={{ ...product, image: product.coverImage || product.image }}
                                        />
                                    ))
                                ) : (
                                    <div className="products-page__empty">
                                        <p>No products match your filters.</p>
                                    </div>
                                )}
                            </div>

                            {totalPages > 1 && (
                                <nav className="products-page__pagination" aria-label="Product pagination">
                                    <button
                                        type="button"
                                        className="products-page__pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage <= 1}
                                        aria-label="Previous page"
                                    >
                                        ← Prev
                                    </button>
                                    <span className="products-page__pagination-info">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="products-page__pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage >= totalPages}
                                        aria-label="Next page"
                                    >
                                        Next →
                                    </button>
                                </nav>
                            )}
                        </>
                    )}
                </section>
            </main>
            <footer>
                <Footer />
            </footer>
        </>
    );
};

export default Products;
